// Third-party
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import deepmerge from 'deepmerge';
import Highcharts, { Point, SeriesScatterOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';

// Components
import Crosshair from '../../../../views/components/charts/scatter-plot/Crosshair';
import * as ProposalNS from '../../Proposal';
import AccessibilityDescription from '../misc/AccessibilityDescription';

// Utils
import dom from '../../../../utils/dom';
import { formatLocalizedString, formatNumber, FormattedMessage } from '../../../../utils/i18n';
import { convertConfigOptionsToHighcharts } from '../../../../utils/charts';

// Types
import ScatterPlotData from '@/types/scatter-plot-data';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';

// Creating type aliases to centralize Highcharts dependency in ProposalScatterPlot.tsx
export type ScatterPlotOptions = Highcharts.Options;

type AxisConfig = {
  min?: number;
  max?: number;
  tickInterval?: number;
  tickPixelInterval?: number;
};

type Props = {
  dark?: boolean;
  data: ScatterPlotData[];
  daoChartOptions?: Partial<ScatterPlotOptions>;
  configOptions: Partial<ProposalNS.ScatterPlotConfigOptions>;
  locale?: Locale;
  accessibilityDescription?: LocalizedString;
  toolbarOption?: ProposalToolbarOption;
};

type State = {
  chartOptions: ScatterPlotOptions;
  svgCrosshairs: React.ReactElement[];
  axes?: {
    x: AxisConfig;
    y: AxisConfig;
  };
};

class ProposalScatterPlot extends Component<Props, State> {
  chartRef = React.createRef<HighchartsReact>();

  static defaultProps: Props = {
    configOptions: {},
    data: [],
  };

  readonly DEFAULT_CHART_OPTIONS: ScatterPlotOptions = {
    chart: {
      type: 'scatter',
      animation: false,
    },
    series: [],
    tooltip: {
      enabled: true,
      useHTML: true,
      backgroundColor: '#000000',
      borderColor: '#000000',
      borderRadius: 6,
      shadow: false,
      valueDecimals: 2,
      style: {
        color: '#ffffff',
      },
    },
    xAxis: {
      gridLineWidth: 1,
      floor: 0, // Standard deviation cannot be negative.
    },
    yAxis: {
      gridLineWidth: 1,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          states: {
            hover: {
              radiusPlus: 0,
            },
          },
        },
      },
      series: {
        animation: false,
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    navigator: { enabled: false },
    credits: { enabled: false },
    legend: { enabled: false },
    title: {
      text: undefined,
    },
  };

  constructor(props: Props) {
    super(props);
    const config = convertConfigOptionsToHighcharts('scatter', props.configOptions, props.locale);
    const options = deepmerge(config, this.DEFAULT_CHART_OPTIONS);

    this.state = {
      chartOptions: deepmerge(options, props.daoChartOptions || {}),
      svgCrosshairs: [],
    };
  }

  componentDidMount() {
    this.updateChart();

    window.addEventListener('resize', this.onResize);

    setTimeout(() => {
      dom.fireResizeEvent();
    }, 100);
  }

  onResize = debounce(() => this.adjustAxes(), 200);

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.data !== prevProps.data) {
      this.updateChart();
    }

    this.reorderGraphSvgLayers();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  adjustAxes = () => {
    this.setState(
      () => ({
        axes: this.calculateAxes(),
      }),
      () => {
        // Reposition crosshairs after axes have been recalculated
        this.repositionCrosshairs();
      },
    );
  };

  repositionCrosshairs = () => {
    setTimeout(() => {
      this.focusDataPoints();
    }, 0);
  };

  calculateAxisBounds = (data: ScatterPlotData[], path: string, minRange?: number) => {
    const reducedValues = data.reduce(
      (values: { min: number; max: number }, datum: ScatterPlotData) => {
        if (!datum || !datum.data || !datum.data[0]) {
          return values;
        }

        const pathValue = get(datum.data[0], path);
        if (pathValue == null) {
          return values;
        }

        return {
          min: Math.min(pathValue, values.min),
          max: Math.max(pathValue, values.max),
        };
      },
      { min: Infinity, max: -Infinity },
    );

    const range = reducedValues.max - reducedValues.min;
    if (minRange != null && range < minRange) {
      const mean = (reducedValues.max + reducedValues.min) / 2;

      return {
        min: mean - minRange / 2,
        max: mean + minRange / 2,
      };
    }

    // We're expanding the bounds by 10% to generate padding, preventing data points from being on the edges of the chart.
    const CHART_PADDING_RATIO = 0.1;
    const absolutePadding = (reducedValues.max - reducedValues.min) * CHART_PADDING_RATIO;

    return {
      min: reducedValues.min - absolutePadding,
      max: reducedValues.max + absolutePadding,
    };
  };

  getXAxisMinMax = (data: ScatterPlotData[]) => {
    const MIN_RANGE = 10;
    return this.calculateAxisBounds(data, 'x', MIN_RANGE);
  };

  getYAxisMinMax = (data: ScatterPlotData[]) => {
    const MIN_RANGE = 10;
    return this.calculateAxisBounds(data, 'y', MIN_RANGE);
  };

  calculateAxes = () => {
    const { data } = this.props;

    if (!this.chartRef.current || !this.chartRef.current || !this.chartRef.current.container.current) {
      return undefined;
    }

    const container = this.chartRef.current.container.current;
    const chartArea = container.getElementsByClassName('highcharts-plot-background')[0] as SVGRectElement;
    const { width, height } = chartArea.getBBox();

    const xAxis = this.getXAxisMinMax(data);
    const yAxis = this.getYAxisMinMax(data);

    const xRange = xAxis.max - xAxis.min;
    const yRange = yAxis.max - yAxis.min;

    let axes;

    const NUM_Y_CELLS = 5; // Number of grid cells on the y-axis - forced value.

    // If x:y ratio is favored for x-axis compared to chart size, use the x-axis as starting point for axes calculation.
    if (xRange / yRange > width / height) {
      // Calculate the pixel interval between each ticks on the y-axis
      const tickPixelInterval = height / (NUM_Y_CELLS - 1);

      // Calculate the tick value interval from range of x-values and width of chart
      const tickInterval = Math.ceil((tickPixelInterval / width) * xRange);

      // Calculate new values range on y from height and previously calculated intervals
      const newYRange = (height / tickPixelInterval) * tickInterval;

      // Calculate new min on y-axis
      yAxis.min -= (newYRange - yRange) / 2;

      axes = {
        x: {
          min: xAxis.min,
          max: xAxis.max,
          tickInterval,
          tickPixelInterval,
        },
        y: {
          min: yAxis.min,
          max: yAxis.min + newYRange,
          tickInterval,
          tickPixelInterval,
        },
      };
    } else {
      // Otherwise, base calculations from y-axis.

      // Calculate tick value interval from y-axis
      const tickInterval = Math.ceil(yRange / (NUM_Y_CELLS - 1));

      // Calculate tick pixel interval from graph height and tick interval
      const tickPixelInterval = height / (yRange / tickInterval);

      // Calculate new values range on x from width and previously calculated intervals
      const newXRange = (width / tickPixelInterval) * tickInterval;

      // Calculate new min on x-axis
      xAxis.min -= (newXRange - xRange) / 2;

      axes = {
        x: {
          min: xAxis.min,
          max: xAxis.min + newXRange,
          tickInterval,
          tickPixelInterval,
        },
        y: {
          min: yAxis.min,
          max: yAxis.max,
          tickInterval,
          tickPixelInterval,
        },
      };
    }

    return axes;
  };

  getChartOptions = (): ScatterPlotOptions => {
    const { locale, configOptions } = this.props;
    const { axes, chartOptions } = this.state;

    return deepmerge<ScatterPlotOptions>(chartOptions, {
      xAxis: {
        title: {
          text: configOptions.xAxis && configOptions.xAxis.title && formatLocalizedString(configOptions.xAxis.title.text, locale),
        },
        labels: {
          formatter() {
            return formatNumber(this.value, 1, locale);
          },
        },
        ...(axes ? axes.x : {}),
      },
      yAxis: {
        title: {
          text: configOptions.yAxis && configOptions.yAxis.title && formatLocalizedString(configOptions.yAxis.title.text, locale),
        },
        labels: {
          formatter() {
            return formatNumber(this.value, 1, locale);
          },
        },
        ...(axes ? axes.y : {}),
      },
    });
  };

  reorderGraphSvgLayers = () => {
    /**
     * The order of the layers somehow gets mixed up while adding the crosshairs. Luckily,
     * Highcharts uses a 'data-z-index' property to sort the layers, so we'll simpy make
     * sure that the index is respected on every update of the chart.
     */
    if (!this.chartRef.current || !this.chartRef.current.container.current) {
      return;
    }

    const container = this.chartRef.current.container.current.getElementsByClassName('highcharts-root')[0];
    if (!container) {
      return;
    }

    const getZIndex = (element: HTMLElement) => {
      const zIndexAttr = element.dataset && element.dataset.zIndex;
      return zIndexAttr ? parseInt(zIndexAttr, 10) : 0;
    };

    const children = [].slice.call(container.children);
    children.sort((a: HTMLElement, b: HTMLElement) => getZIndex(a) - getZIndex(b));

    container.childNodes.forEach(node => node.remove());

    for (const child of children) {
      container.appendChild(child);
    }
  };

  getCrosshairsForPoints = (points: Point[]) => {
    if (!this.chartRef.current) {
      return [];
    }

    const container = this.chartRef.current.container.current;
    if (!container) {
      return [];
    }

    const chartBox = container.getElementsByClassName('highcharts-plot-background')[0] as SVGRectElement;
    const containerBBox = chartBox.getBBox();

    const crosshairStyles = {
      transform: `translate(${containerBBox.x}px, ${containerBBox.y}px)`,
    };

    return points
      .map(p => {
        if (!p.graphic) {
          return null;
        }

        const { width, height, x, y } = p.graphic.element.getBBox();
        const center = {
          x: x + width / 2,
          y: y + height / 2,
        };

        return <Crosshair style={crosshairStyles} key={p.key} center={center} container={containerBBox} color={p.color.toString()} />;
      })
      .filter(element => !!element) as JSX.Element[];
  };

  formatScatterPlotData = (series: ScatterPlotData[]): SeriesScatterOptions[] =>
    series.map(
      s =>
        ({
          data: s.data,
          key: s.key,
          type: 'scatter',
        }) as SeriesScatterOptions,
    );

  updateChart = () => {
    const { data } = this.props;
    const { chartOptions } = this.state;

    const updatedOptions = { ...chartOptions, series: this.formatScatterPlotData(data) } as ScatterPlotOptions;
    this.adjustAxes();
    this.setState({ chartOptions: updatedOptions });
  };

  injectCrosshairs = () => {
    const { svgCrosshairs } = this.state;

    if (!svgCrosshairs.length || !this.chartRef.current || !this.chartRef.current.container.current) {
      return null;
    }

    /**
     * The only way to ensure that the crosshairs do not render over the tooltips is to
     * inject them directly into the Highcharts container and to adjust the z-index properties afterwards.
     */
    const container = this.chartRef.current.container.current.getElementsByClassName('highcharts-root')[0];
    if (!container) {
      return null;
    }

    return ReactDOM.createPortal(svgCrosshairs, container);
  };

  unfocusDataPoints(keys: string[]) {
    for (const key of keys) {
      this.findDataPoints(key).forEach(p => p.setState(''));
    }
  }

  focusDataPoints() {
    const { data } = this.props;

    const crosshairKeys = data
      .map(datum => {
        if (datum.showCrosshair === undefined || datum.showCrosshair === false || datum.key === undefined) {
          return '';
        }
        return datum.key;
      })
      .filter(datum => datum.length !== 0);

    if (crosshairKeys.length === 0) {
      return;
    }

    const points = crosshairKeys.reduce((acc, key) => acc.concat(this.findDataPoints(key)), [] as Point[]);

    const svgCrosshairs = this.getCrosshairsForPoints(points);
    this.setState({ svgCrosshairs });
  }

  findDataPoints(key: string) {
    if (!this.chartRef.current) {
      return [];
    }

    return this.chartRef.current.chart.series.reduce((acc, serie) => acc.concat(serie.points.filter(p => p.key === key)), [] as Point[]);
  }

  getDataItems(itemsData?: ScatterPlotData[]) {
    return itemsData?.map(item => item.data[0].name || '') || [];
  }

  render() {
    const { dark, locale, data, accessibilityDescription, toolbarOption } = this.props;

    return (
      <div className={classNames('scatter-plot', { dark })}>
        {accessibilityDescription && (
          <AccessibilityDescription description={accessibilityDescription} items={this.getDataItems(data)} locale={locale} toolbarOption={toolbarOption} />
        )}

        {this.injectCrosshairs()}
        {data && data.length > 0 ? (
          <HighchartsReact
            ref={this.chartRef}
            containerProps={{ className: 'highcharts-react-container' }}
            highcharts={Highcharts}
            options={this.getChartOptions()}
          />
        ) : (
          <div className="empty-chart flex-fill">
            <FormattedMessage id="Charts.EmptyPeriod" locale={locale} />
          </div>
        )}
      </div>
    );
  }
}

export type ScatterPlotProps = Props;

export default ProposalScatterPlot;
